import { useSmartMutation, useSmartQuery } from "@shared/api/queries/hooks";
import { queryClient } from "@shared/api/queries/queryClient";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { useToast } from "@shared/hooks/use-toast";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { toast } from "@shared/ui/use-toast";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { axSystemsApi } from "./api";

export const useGetFacilities = () => {
  const remoteUtilityId = getRemoteUtilityId();
  const isAllData = 1;
  return useSmartQuery(
    QueryKeyFactory.module.ax.systemManagement.getFacilities(
      remoteUtilityId,
      isAllData
    ),
    () => axSystemsApi.getFacilities(remoteUtilityId, isAllData),
    {
      enabled: !!remoteUtilityId,
    }
  );
};

export const useGetChoices = (type: string) => {
  return useSmartQuery(
    QueryKeyFactory.module.ax.systemManagement.getChoices(type),
    () => axSystemsApi.getChoices(type),
    {
      enabled: true,
    }
  );
};

export const useGetAllSystems = (filters?: Record<string, any>) => {
  const remoteUtilityId = getRemoteUtilityId();
  const isPaginationRequired = 1;

  return useSmartQuery(
    QueryKeyFactory.module.ax.systemManagement.getAllSystems(
      remoteUtilityId,
      isPaginationRequired,
      filters
    ),
    () =>
      axSystemsApi.getAllSystems(
        remoteUtilityId,
        isPaginationRequired,
        filters
      ),
    {
      enabled: !!remoteUtilityId,
    }
  );
};

export const useGetDashboardData = () => {
  const remoteUtilityId = getRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.ax.systemManagement.getDashboardData(
      remoteUtilityId
    ),
    () => axSystemsApi.getDashboardData(remoteUtilityId),
    {
      enabled: !!remoteUtilityId,
    }
  );
};

export const useCreateSystems = () => {
  const { toast } = useToast();
  const remoteUtilityId = getRemoteUtilityId();

  return useSmartMutation(
    (payload: any) => axSystemsApi.createSystems(payload),
    {
      onSuccess: (_data, payload) => {
        toast({
          title: "System Created",
          description: "System created successfully.",
        });
        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.ax.systemManagement
            .getAllSystems(remoteUtilityId, 1)
            .slice(0, 4),
        });
      },
      onError: (e) => {
        toast({ title: e?.message, variant: "destructive" });
      },
    }
  );
};

export const useUpdateSystems = (systemId?: string | number) => {
  const { toast } = useToast();
  const remoteUtilityId = getRemoteUtilityId();

  return useSmartMutation(
    (payload: any) => axSystemsApi.updateSystems(systemId, payload),
    {
      onSuccess: (_data, payload) => {
        toast({
          title: "System Updated",
          description: "System updated successfully.",
        });
        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.ax.systemManagement
            .getAllSystems(remoteUtilityId, 1)
            .slice(0, 4),
        });
        queryClient.removeQueries({
          queryKey:
            QueryKeyFactory.module.ax.systemManagement.getDashboardData(
              remoteUtilityId
            ),
        });
      },
      onError: (e) => {
        toast({ title: e?.message, variant: "destructive" });
      },
    }
  );
};

export const useUpdateDocuments = (type: string) => {
  const { toast } = useToast();
  const remoteUtilityId = getRemoteUtilityId();

  return useSmartMutation(
    ({
      systemId,
      formData,
    }: {
      systemId: number;
      formData: FormData;
    }): Promise<any> => {
      return axSystemsApi.updateDocuments(systemId, formData);
    },
    {
      onSuccess: (data, { systemId }) => {
        if (type === "documents") {
          toast({
            title: "Success!",
            description: "Files uploaded successfully",
          });
        } else {
          toast({
            title: "Success!",
            description: "System updated successfully",
          });
        }

        // Invalidate specific facility and list
        queryClient.invalidateQueries({
          queryKey:
            QueryKeyFactory.module.ax.systemManagement.getSystemDataById(
              systemId
            ),
        });

        queryClient.refetchQueries({
          queryKey: [
            "ax",
            "systemManagement",
            "getAllSystems",
            remoteUtilityId,
            1,
            "",
          ],
        });
      },

      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to update documents",
          variant: "destructive",
        });
      },
    }
  );
};

export const useGetAllUserInfo = () => {
  const remoteUtilityId = getRemoteUtilityId();
  const role = "Plant Supervisor";
  return useSmartQuery(
    QueryKeyFactory.module.ax.systemManagement.getAllUserInfo(
      remoteUtilityId,
      role
    ),
    () => axSystemsApi.getAllUserInfo(remoteUtilityId, role),
    {
      enabled: !!remoteUtilityId,
    }
  );
};

export const useGetSystemDataById = (id: number) => {
  return useSmartQuery(
    QueryKeyFactory.module.ax.systemManagement.getSystemDataById(id),
    () => axSystemsApi.getSystemDataById(id),
    {
      enabled: !!id,
    }
  );
};

export const useGetSystemDataByIdonEdit = (
  id: number,
  { enabled }: { enabled?: boolean }
) => {
  return useSmartQuery(
    QueryKeyFactory.module.ax.systemManagement.getSystemDataById(id),
    () => axSystemsApi.getSystemDataById(id),
    {
      enabled: !!id && enabled,
    }
  );
};

export const useGetSystemTabDataById = (id: number, tab_name: string) => {
  const remoteUtilityId = getRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.ax.systemManagement.getAllSystemTabDataById(
      id,
      tab_name,
      remoteUtilityId
    ),
    () => axSystemsApi.getAllSystemTabDataById(id, tab_name, remoteUtilityId),
    {
      enabled: !!id && !!tab_name && !!remoteUtilityId,
    }
  );
};

export const useGetSystemAssetsTabDataById = (id: number, tab_name: string) => {
  const isPaginationRequired = 1;
  return useSmartQuery(
    QueryKeyFactory.module.ax.systemManagement.getAllSystemAssetTabDataById(
      id,
      tab_name,
      isPaginationRequired
    ),
    () =>
      axSystemsApi.getSystemAssetsTabDataById(
        id,
        tab_name,
        isPaginationRequired
      ),
    {
      enabled: !!id && !!tab_name,
    }
  );
};

export const useGetAllUtilityServices = () => {
  const remoteUtilityId = getRemoteUtilityId();
  const utility_service = "Water";
  return useSmartQuery(
    QueryKeyFactory.module.ax.systemManagement.getAllUtilityServices(
      remoteUtilityId,
      utility_service
    ),
    () => axSystemsApi.getAllUtilityServices(remoteUtilityId, utility_service),
    {
      enabled: !!remoteUtilityId && !!utility_service,
    }
  );
};

export const useGetAllCriticality = () => {
  const remoteUtilityId = getRemoteUtilityId();
  const show_inactive = true;
  return useSmartQuery(
    QueryKeyFactory.module.ax.systemManagement.getAllCriticality(
      remoteUtilityId,
      show_inactive
    ),
    () => axSystemsApi.getAllcriticality(remoteUtilityId, show_inactive),
    {
      enabled: !!remoteUtilityId && !!show_inactive,
    }
  );
};

export const useGetAllServiceOrder = (
  id: number,
  search: string,
  pagination: { currentPage: number; limit: number }
) => {
  const remoteUtilityId = getRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.ax.systemManagement.getAllServiceOrder(
      remoteUtilityId,
      id,
      search,
      pagination
    ),
    () =>
      axSystemsApi.getAllServiceOrder(remoteUtilityId, id, search, pagination),
    {
      enabled: !!remoteUtilityId && !!id,
    }
  );
};

export const useGetSystemComplianceTabDataById = (
  id: number,
  entityType: number
) => {
  const remoteUtilityId = getRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.ax.systemManagement.getSystemComplianceTabDataById(
      remoteUtilityId,
      id
    ),
    () =>
      axSystemsApi.getSystemComplianceTabDataById(
        entityType,
        id,
        remoteUtilityId
      ),
    {
      enabled: !!remoteUtilityId && !!id,
    }
  );
};

export const useGetSystemComplianceHistoryTabDataById = (
  id: number,
  entityType: number
) => {
  const remoteUtilityId = getRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.ax.systemManagement.getSystemComplianceHistoryTabDataById(
      entityType,
      id,
      remoteUtilityId
    ),
    () =>
      axSystemsApi.getSystemComplianceHistoryTabDataById(
        entityType,
        id,
        remoteUtilityId
      ),
    {
      enabled: !!id && !!remoteUtilityId,
    }
  );
};

export const useGetAllAssets = (
  page: number,
  limit: number,
  system: number,
  search: string
) => {
  const remoteUtilityId = getRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.ax.systemManagement.getAllAssets(
      remoteUtilityId,
      page,
      limit,
      system,
      search
    ),
    () =>
      axSystemsApi.getAllAssets(remoteUtilityId, page, limit, system, search),
    {
      enabled: !!remoteUtilityId,
    }
  );
};

export const useGetSystemType = (
  configLevel: string,
  showInactive: boolean
) => {
  const remoteUtilityId = getRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.ax.systemManagement.getSystemType(
      remoteUtilityId,
      configLevel,
      showInactive
    ),
    () =>
      axSystemsApi.getSystemType(remoteUtilityId, configLevel, showInactive),
    {
      enabled: !!remoteUtilityId,
    }
  );
};

export const useAssetsList = (filters: any) => {
  const remoteUtilityId = useRemoteUtilityId();
  const search = filters.searchData || "";

  return useSmartQuery(
    QueryKeyFactory.module.ax.systemManagement.assets.list(
      remoteUtilityId,
      search,
      filters.systemId,
      "system-asset",
      filters.assetType
    ),
    () => axSystemsApi.getAssets(remoteUtilityId, filters),
    {
      enabled: !!remoteUtilityId,
    }
  );
};

export const useDownloadBill = () => {
  const { toast } = useToast();

  return useSmartMutation(
    (dataTypeName: string) => axSystemsApi.getDownloadBill(dataTypeName),
    {
      onSuccess: (blob: Blob, dataTypeName: string) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${dataTypeName}.csv`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        toast({
          title: "Download Complete",
          description: "CSV File has been downloaded successfully.",
        });
      },
      onError: (error: any) => {
        toast({
          title: "Download Failed",
          description: error?.message || "Failed to download CSV File",
          variant: "destructive",
        });
      },
    }
  );
};

export const useUploadEnergyData = (id: number) => {
  const { toast } = useToast();
  const remoteUtilityId = getRemoteUtilityId();
  const tab_name = "performance";

  return useSmartMutation(
    (formData: FormData): Promise<any> => {
      return axSystemsApi.uploadEnergyData(formData);
    },
    {
      onSuccess: (data, formData) => {
        toast({
          title: "Success!",
          description: "Energy Data Uploaded successfully",
        });

        // Invalidate specific facility and list
        queryClient.invalidateQueries({
          queryKey:
            QueryKeyFactory.module.ax.systemManagement.getAllSystemTabDataById(
              id,
              tab_name,
              remoteUtilityId
            ),
        });
      },

      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to upload energy data",
          variant: "destructive",
        });
      },
    }
  );
};

export const useGetAllEnergyConsumptionHistory = (openmodal: boolean) => {
  const remoteUtilityId = useRemoteUtilityId();

  return useSmartQuery(
    QueryKeyFactory.module.ax.systemManagement.getAllEnergyConsumptionHistory(
      remoteUtilityId
    ),
    () => axSystemsApi.getAllEnergyConsumptionHistory(remoteUtilityId),
    {
      enabled: !!remoteUtilityId && openmodal,
    }
  );
};

export const useDeleteSystem = () => {
  const remoteUtilityId = useRemoteUtilityId();

  return useSmartMutation(
    ({ systemId }: { systemId: string }): Promise<any> => {
      return axSystemsApi.deleteSystem(systemId, remoteUtilityId);
    },
    {
      onSuccess: () => {
        toast({
          title: "Success!",
          description: "System deleted successfully",
        });

        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.ax.systemManagement
            .getAllSystems(Number(remoteUtilityId), 1)
            .slice(0, 5),
        });
      },

      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to delete system",
          variant: "destructive",
        });
      },
    }
  );
};

export const useSystemParameters = (systemType: string) => {
  const remoteUtilityId = useRemoteUtilityId();

  return useSmartQuery(
    QueryKeyFactory.module.ax.systemManagement.systemParameters(systemType),
    () => axSystemsApi.getSystemParameters(remoteUtilityId, systemType),
    {
      enabled: !!remoteUtilityId && !!systemType,
    }
  );
};
